import * as React from "react";
import Papa, { ParseResult } from "papaparse";
import { Alert, Button, Input, Modal, notification } from "antd";
import { cancelSentTask, fetchSentPendingTasks, inviteUsers } from "../../services/task";
import { Tooltip } from "../../components/Tooltip";
import { Table } from "../../newcomponents/table/table";
import { Task } from "../../schema/types/task";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { validationFns } from "../../components/form/validations";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { addNotification } from "../../store/notificationDrawerSlice";
import { getDefaultKindSelectState, KindSelect, KindSelectState } from "../../components/antd/kindselect/KindSelect";

interface UserInvitation {
  email: string;
  group: string;
}

export const Invite: React.FC = () => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);
  const page = typeof window !== "undefined" ? window.location.pathname : "";

  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = React.useState(false);
  const [invites, setInvites] = React.useState<Task[]>([]);
  const [groupName, setGroupName] = React.useState("viewers");
  const [groupState, setGroupState] = React.useState<KindSelectState>(getDefaultKindSelectState({ value: "viewers" }));
  const [invitations, setInvitations] = React.useState<UserInvitation[]>([]);
  const [selectedTasks, setSelectedTasks] = React.useState<Task[]>([]);
  const [selectionsToRemove, setSelectionsToRemove] = React.useState<string[]>([]);
  const [alerts, setAlerts] = React.useState<React.ReactNode[]>([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const [email, setEmail] = React.useState("");

  React.useEffect(() => {
    updateInvites();
  }, []);

  async function updateInvites() {
    const tasks = await fetchSentPendingTasks();
    setInvites(tasks);
  }

  function addEmail() {
    if (invitations.some((i) => i.email === email)) return;
    const _invitations = [...invitations];
    var newInvitation = { email: email, group: groupState.link };
    _invitations.push(newInvitation);
    setInvitations(_invitations);
    setEmail("");
  }

  function removeEmail(email: string) {
    setInvitations((inv) => inv.filter((i) => i.email !== email));
  }

  function clearInvitations(): void {
    setAlerts([]);
    setInvitations([]);
  }

  async function onCSVChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAlerts([]);

    if (!e.target.value || !e.target.files || e.target.files.length < 1) {
      e.target.value = "";
      notification.warning({
        message: "Failed",
        description: "No file selected or the input is invalid.",
      });
      return;
    }

    getInvitationsFromCsvContent(e.target.files[0]);
    e.target.value = "";
  }

  function getInvitationsFromCsvContent(file: File): void {
    if (!file) {
      notification.warning({
        message: "Failed",
        description: "Selected file is invalid.",
      });
      return;
    }

    const rawInvitations: UserInvitation[] = [];
    const duplicateEmails = new Set<string>();

    const existingEmails = new Set(invitations.map((i: UserInvitation): string => i.email));

    Papa.parse<string[]>(file, {
      delimiter: "", // Auto detect delimiter
      skipEmptyLines: true,
      complete: (results: ParseResult<string[]>): void => {
        // Ignore empty CSV
        if (results.data.length == 0) {
          notification.error({
            message: "Empty CSV File",
            description: "The selected CSV file contains no data. Please upload a non-empty CSV file.",
          });
          return;
        }

        for (const row of results.data) {
          let email = row[0];
          if (!email || !email.includes("@")) {
            continue;
          }
          email = email.toLowerCase();

          if (!validateEmail(email)) {
            continue;
          }

          if (existingEmails.has(email)) {
            duplicateEmails.add(email);
            return;
          }

          const groupName = row[1];
          if (groupName) {
            rawInvitations.push({ email, group: `/org/${org}/group/${groupName}` });
          } else {
            rawInvitations.push({ email, group: `/org/${org}/group/viewers` });
          }

          existingEmails.add(email);
        }

        setInvitations([...invitations, ...rawInvitations]);

        const newAlerts: React.ReactElement[] = [];

        if (duplicateEmails.size > 0) {
          newAlerts.push(
            <Alert
              key="duplicateEmails"
              message="Duplicate Emails"
              description={
                <div>
                  <div>The following email addresses were ignored because they are already in the list:</div>
                  <ul className="flex flex-col gap-2 list-disc list-inside">
                    {Array.from(duplicateEmails).map((emailKey, index) => (
                      <li key={emailKey + String(index)}>{emailKey}</li>
                    ))}
                  </ul>
                </div>
              }
              style={{ maxWidth: "100%", width: "100%" }}
              closable={true}
            />,
          );
        }

        if (rawInvitations.length === 0 && duplicateEmails.size === 0) {
          newAlerts.push(
            <Alert
              key="noInvitations"
              message="No Valid Invitations Found"
              description="The uploaded CSV file is empty or does not contain any valid email addresses for invitations."
              style={{ maxWidth: "100%", width: "100%" }}
              closable={true}
            />,
          );
        }

        setAlerts(newAlerts);
      },
      error: (error: Error) => {
        notification.error({
          message: "CSV Parsing Error",
          description: `An error occurred while processing the CSV file: ${error.message}.`,
        });
      },
    });
  }

  async function invite() {
    setAlerts([]);

    try {
      setIsLoading(true);

      const succeededEmails: string[] = [];
      const failedEmails: { email: string; message: string }[] = [];

      for (let invitation of invitations) {
        let inviteRes;
        try {
          inviteRes = await inviteUsers([invitation.email], invitation.group);
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          failedEmails.push({ email: invitation.email, message: errorMessage });
          continue;
        }
        for (let entry of Object.entries(inviteRes.invitations)) {
          const email: string = entry[0];
          succeededEmails.push(email);
        }

        for (let entry of Object.entries(inviteRes.errors)) {
          const email: string = entry[0];
          const messageHolder: any = entry[1];
          failedEmails.push({ email, message: messageHolder.message.replace(/user/i, email) });
        }
      }

      if (succeededEmails.length > 0) {
        notification.success({
          message: "Success",
          description: (
            <>
              <ul>
                {succeededEmails.map((email, index) => (
                  <li key={email + String(index)} className="block my-2">
                    {email} invited.
                  </li>
                ))}
              </ul>
            </>
          ),
        });
        dispatch(
          addNotification({
            page,
            action: "Invite",
            success: true,
            message: "Success",
            details: succeededEmails,
          })
        );
      }
      if (failedEmails.length > 0) {
        notification.warning({
          message: "Failure",
          description: (
            <>
              <span>Failed Invitations:</span>
              <ul>
                {failedEmails.map(({ email, message }, index) => (
                  <li key={email + String(index)} className="my-2">
                    <span className="block text-sm">{email}</span>
                    <span className="block text-xs">
                      {message}
                      {message.endsWith(".") ? "" : "."}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ),
        });
        dispatch(
          addNotification({
            page,
            action: "Invite",
            success: false,
            message: "Failure",
            details: failedEmails,
          })
        );
      }

      await updateInvites();

      setInvitations([]);
      setEmail("");
      setGroupState(getDefaultKindSelectState({ value: "viewers" }));
      setIsLoading(false);
    } catch (e) {
      console.error("Org InviteUser", e);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      dispatch(
        addNotification({
          page,
          action: "Invite",
          success: false,
          message: errorMessage,
        })
      );
      setIsLoading(false);
    }
  }

  function onDeleteTask() {
    if (selectionsToRemove.length < 1) {
      return;
    }

    setSelectedTasks(invites.filter((task) => selectionsToRemove.includes(task.id)));
    setSelectionsToRemove([]);
  }

  function cancelTaskDeletion(): void {
    setSelectedTasks([]);
    setIsLoadingDelete(false);
  }

  async function confirmTaskDeletion(): Promise<void> {
    const promises: Promise<string | undefined>[] = [];
    const failedCancellations: string[] = [];

    setAlerts([]);
    setIsLoadingDelete(true);

    for (const task of selectedTasks) {
      promises.push(cancelInvitation(task));
    }

    const responses: (string | undefined)[] = await Promise.all(promises);

    for (const response of responses) {
      if (!response) {
        continue;
      }

      failedCancellations.push(response);
    }

    if (failedCancellations.length === 0) {
      notification.success({
        message: "Success",
        description: "Selected invite(s) were cancelled.",
      });
      dispatch(
        addNotification({
          page,
          action: "Cancel Invitation",
          success: true,
          message: "Selected invite(s) were cancelled.",
        })
      );
    } else {
      setAlerts([
        <Alert
          key="invalidInviteCancellations"
          type="error"
          message="Unable to cancel invitation(s)"
          description={
            <div>
              <div>The following invitations were not cancelled for the following errors:</div>
              <ul className="flex flex-col gap-2 list-disc list-inside">
                {failedCancellations.map((error, index) => (
                  <li key={error + String(index)}>{error}</li>
                ))}
              </ul>
            </div>
          }
          style={{ maxWidth: "100%", width: "100%" }}
          closable={true}
        />,
      ]);
      notification.error({
        message: "Failed",
        description: `Unable to cancel invitation(s)`,
      });
      dispatch(
        addNotification({
          page,
          action: "Cancel Invitation",
          success: false,
          message: "Unable to cancel invitation(s)",
        })
      );
    }

    // Refresh pending invites list
    await updateInvites();

    // Close modal
    setSelectedTasks([]);
    setIsLoadingDelete(false);
  }

  async function cancelInvitation(task: Task): Promise<string | undefined> {
    try {
      if (task.status !== "pending") {
        return `${task.targetEmail} - invitation is already ${task.status === "canceled" ? "cancelled" : "completed"}`;
      }

      await cancelSentTask(task.id);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      return `${task.targetEmail} - ${errorMessage}`;
    }

    return undefined;
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>): void {
    setIsDragging(false);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragging(false);

    const files: FileList = event.dataTransfer.files;

    if (files.length == 0) {
      notification.error({ message: "No files were detected in the drop. Please try again." });
    }

    // Handle CSV
    getInvitationsFromCsvContent(files[0]);
  }

  function validateEmail(email: string): boolean {
    if (validationFns().email(email) !== true) {
      return false;
    }

    return true;
  }
  const isEmailValid = React.useMemo(() => {
    return validateEmail(email);
  }, [email]);

  return (
    <>
      <div className="mb-4 text-2xl">Invite Users</div>
      <div>Invite users to this org and associate them with an optional group.</div>
      <div style={{ width: 1000 }}>
        <div>Add Email to Invitation List</div>
        <div className="flex mt-2">
          <Input
            name="email"
            placeholder="Email"
            className="mr-4"
            style={{ width: 450 }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <KindSelect
            onStateChange={setGroupState}
            kind={"group"}
            value={groupName}
            onChange={setGroupName}
            optionValue="name"
            style={{ width: 200 }}
          />
        </div>
        <div className="mt-2 flex justify-between" style={{ width: 666 }}>
          {!isEmailValid ? (
            <div className="color-danger text-sm">{`Value is not a valid email`}</div>
          ) : invitations.some((i) => i.email === email) ? (
            <div className="color-danger text-sm">Email is already in the invitation list.</div>
          ) : (
            <div />
          )}
          <Button
            disabled={!isEmailValid || invitations.some((i) => i.email === email)}
            onClick={addEmail}
            style={{ width: 200 }}
            className="ml-2 action"
            type={"primary"}
          >
            Add to Invitation List
          </Button>
        </div>
        <div className="flex items-center justify-between mt-4 mb-2">
          <div>Invitation List</div>
          <div className="flex items-center gap-2">
            <label
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  document.getElementById("csvFile")?.click();
                }
              }}
              className="cursor-pointer ngfocus color-link text-sm"
            >
              Upload from CSV{" "}
              <input
                id={"csvFile"}
                accept={".csv"}
                onChange={onCSVChange}
                type={"file"}
                className="invisible"
                multiple={false}
                style={{ width: 0 }}
              />
            </label>
            <Button type={"default"} danger size="small" disabled={invitations.length === 0} onClick={clearInvitations}>
              Clear List
            </Button>
          </div>
        </div>
        {invitations.length === 0 ? null : (
          <div
            className="flex items-center w-full mt-2 px-4 py-1 table-labels"
            style={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
          >
            <div className={"w-6/12"}>
              <span>Email</span>
            </div>
            <div className={"w-3/12"}>
              <span>Group</span>
            </div>
            <div className={"w-3/12"}>
              <span></span>
            </div>
          </div>
        )}
        {invitations.length === 0 ? (
          <div
            className={`relative ${isDragging ? "bg-black" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="border rounded p-8 mb-6">
              <div className="text-center text-2xl">
                {isDragging ? "Drop Here" : "Add invite or Drag & Drop CSV File"}
              </div>
            </div>
          </div>
        ) : null}
        <div className="overflow-y-auto" style={{ maxHeight: 565 }}>
          {invitations.map((invitation, index) => (
            <div key={invitation.email + String(index)} className="flex items-center w-full border-b h-12 px-4">
              <div className={"w-6/12"}>
                <span>{invitation.email}</span>
              </div>
              <div className={"w-3/12"}>
                <Tooltip title={invitation.group || "None"}>
                  <span>{invitation.group !== "none" ? invitation.group.split("/")[4] : "None"}</span>
                </Tooltip>
              </div>
              <div className={"w-3/12 flex items-center justify-end"}>
                <button type={"button"} className="focus color-danger" onClick={() => removeEmail(invitation.email)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        {invitations.length === 0 ? null : (
          <div className="flex items-center justify-end my-6">
            <Button
              style={{ width: 200 }}
              disabled={isLoading || invitations.length < 1 || email.length > 0}
              loading={isLoading}
              type={"primary"}
              onClick={invite}
            >
              Confirm Invitations
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 w-full">{alerts}</div>
      <div className="mt-4" style={{ maxWidth: 1000 }}>
        <Table<Task>
          tableId="task-invites-update1"
          title={"Pending Invites"}
          selectMode={"multi"}
          selectKey={"id"}
          selections={selectionsToRemove}
          onSelectionsChange={setSelectionsToRemove}
          data={invites}
          columns={[
            { id: "description", label: "Message" }, //
            { id: "targetEmail", label: "Email" },
            CreatedColumn(),
          ]}
          headerRenderer={() => (
            <>
              <Button type={"default"} danger disabled={selectionsToRemove.length < 1} onClick={onDeleteTask}>
                Cancel Invitations
              </Button>
            </>
          )}
        />
      </div>
      {selectedTasks.length > 0 ? (
        <Modal
          title="Are you sure you want to delete invitation(s)?"
          open={true}
          maskClosable={false}
          destroyOnHidden
          cancelText={`Close`}
          okText={`Delete`}
          cancelButtonProps={{ type: "default", disabled: isLoadingDelete }}
          okButtonProps={{ type: "primary", danger: true, disabled: isLoadingDelete, loading: isLoadingDelete }}
          onCancel={cancelTaskDeletion}
          onOk={confirmTaskDeletion}
        />
      ) : null}
    </>
  );
};
